import { Injectable } from '@angular/core';
import { AnalyticsBrowser, Context } from '@segment/analytics-next';
import { AnalyticsDoubleTrackProperties } from 'src/app/core/services/analytics/models/analytics-double-track-properties.model';
import { AnalyticsEventGlobalProperties } from 'src/app/core/services/analytics/models/analytics-event-global-properties.model';
import { AnalyticsEventIdentifyProperties } from 'src/app/core/services/analytics/models/analytics-event-identify-properties';
import { AnalyticsEventProperties } from 'src/app/core/services/analytics/models/analytics-event-properties.model';
import { ActionEventParams } from 'src/app/core/services/analytics/models/firebase-action-event-params.model';
import { BrazeContentCardsService } from 'src/app/shared/braze-content-cards/braze-content-cards.service';
import { AccountService } from 'src/app/shared/services/account.service';
import { environment } from 'src/environments/environment';

/**
 * NOTE: IF YOU ARE TRYING TO USE SEGMENT TRACKING - YOU ARE IN THE WRONG PLACE - USE analytics.service.ts
 *
 * This service implements vendor specific tracking and not intended for individual service / component.
 */

@Injectable({
  providedIn: 'root',
})
export class SegmentAnalyticsService {
  // The instance of the Segment Analytics
  private analytics: AnalyticsBrowser;

  // Account type mapping object
  private accountTypeMapping: Record<string, string> = {
    Carer: 'worker',
    Consumer: 'client',
  };

  constructor(
    private accountService: AccountService,
    private brazeContentCardsService: BrazeContentCardsService,
  ) {}

  /**
   * Initialise Segment Analytics service. This method should be called once.
   */
  initialise(): void {
    // Do nothing if the service has already been setup successfully once before.
    if (this.analytics) return;

    this.analytics = this.loadAnalyticsBrowser();

    if (!this.analytics) {
      console.warn('Initialisation of SegmentAnalyticsService failed.');
    }

    // Add source middleware to the analytics instance if it exists.
    if (this.analytics) {
      // Subscribe to trigger the load. Subscription self completes.
      this.brazeContentCardsService.init().subscribe();
      this.addSourceMiddleware();
    }
  }

  /**
   * Clears userId and anonymous_id saved in local storage and cookies.
   */
  reset(): void {
    if (this.analytics) {
      this.analytics.reset();
    }
  }

  /**
   * The track method records any actions/events.
   *
   * @param event - The name of the tracking event.
   * @param properties - A dictionary of properties for the event.
   * @param options - A dictionary of options for the event.
   */
  track(
    event: string,
    properties:
      | ActionEventParams
      | AnalyticsEventProperties
      | AnalyticsDoubleTrackProperties
      | AnalyticsEventGlobalProperties = {},
    options = {},
  ): void {
    if (this.analytics) {
      this.analytics.user().then((user) => {
        // If the user is logged in, inject account_type and user_id into the event properties.
        const globalEventProperties = { ...this.createGlobalProperties(event), ...properties };

        this.analytics.track(event, globalEventProperties, options);
      });
    }
  }

  /**
   * The page method records page views on the app, along with optional extra information about the page being viewed.
   *
   * @param name - The name of the page.
   * @param properties - A dictionary of properties of the page.
   */
  page(name: string, properties: AnalyticsEventProperties = {}): void {
    if (this.analytics) {
      this.analytics.user().then((user) => {
        const globalEventProperties = { ...this.createGlobalProperties(), ...properties };
        const options = {};

        this.analytics.page(name, globalEventProperties, options);
      });
    }
  }

  /**
   * It fills userId property in the Segment payload.
   *
   * Note: When you make an identify call using Segment’s Analytics.js library,
   * Segment saves the userId to the browser cookie, and writes all the user traits in localStorage.
   * This makes it possible to append the user’s data to all subsequent page calls or track calls for the user,
   * so you can properly attribute those actions.
   *
   * @param properties - A AnalyticsEventIdentifyProperties object to pass to segment.
   */
  async identify(properties: AnalyticsEventIdentifyProperties = {}): Promise<Context> {
    const userId = this.accountService.getUser()?.custom?.user_uuid;

    if (this.analytics && !!userId) {
      return this.analytics.identify(userId, properties);
    }
  }

  // Create global properties to inject user_id and account_type.
  private createGlobalProperties(event?: string): AnalyticsEventProperties {
    const currentUser = this.accountService.getUser();
    // If the user is logged in, return account_type and user_id properties.
    if (currentUser) {
      const accountTypeMappingResult = this.accountTypeMapping[currentUser.custom?.account_type];
      const account_type_property_name = ['login', 'Logged In', 'application_complete', 'Signup Completed'].includes(
        event,
      )
        ? 'account_type'
        : 'member_type';
      return {
        [account_type_property_name]: accountTypeMappingResult || currentUser.custom?.account_type?.toLowerCase(),
        // TODO(ES-30990): Cleanup and remove null defaults once issue is resolved (maybe email too)
        user_id: currentUser?.custom?.user_uuid || null,
        email: currentUser?.custom?.email || null,
      };
    }
    return {};
  }

  /**
   * Loads the Segment Analytics JS SDK, proxying through our custom domains.
   * Its worth noting that Segment JS will sideload any JS libraries enabled within the Segment Source, like the Braze SDK.
   *
   * Note that if the environment has not defined custom CDN or API domain name in the environment.ts, then Segment will not be loaded.
   *
   * @returns An AnalyticsBrowser object.
   */
  private loadAnalyticsBrowser(): AnalyticsBrowser {
    if (Object.getOwnPropertyDescriptor(environment, 'segmentConfig')) {
      return AnalyticsBrowser.load(
        {
          writeKey: environment['segmentConfig'].writeKey,
          cdnURL: environment['segmentConfig'].cdnUrl(),
        },
        {
          integrations: {
            'Segment.io': {
              apiHost: environment['segmentConfig'].apiHost(),
              protocol: 'https',
            },
          },
        },
      );
    }
    return null;
  }

  /**
   * Adds a middleware to the analytics instance to modify the userId of an event if the events user_id and userId differ.
   * Also allowing us to track events for a different user than the one currently identified by Segment.
   */
  private addSourceMiddleware(): void {
    this.analytics.addSourceMiddleware(({ payload, next }) => {
      const eventUserId = payload.obj.userId;
      const propertiesUserId = payload.obj.properties?.user_id;

      if (propertiesUserId && eventUserId !== propertiesUserId) {
        payload.obj.userId = propertiesUserId;
      }

      next(payload);
    });
  }
}
